/**
 * Contents rail behaviour for the legal pages (privacy, terms, returns).
 *
 * The rail is these pages' primary navigation: most people arrive looking for
 * one clause, not to read twelve chapters top to bottom. Markup and layout live
 * in sections/pag-legal.liquid. This file adds the four things the rail cannot
 * do for itself, and nothing else — the body copy is not animated, because a
 * policy paragraph arriving with a flourish communicates nothing and holding
 * legal text at opacity 0 to do it is a bad trade.
 *
 *   • A scroll spy, so the rail says where you are. Desktop draws it as a bar
 *     on the rail line; the mobile chip row fills the current chip and pulls it
 *     back into view, which is the only way a twelve-chip row that runs off the
 *     screen stays usable while you read.
 *   • Reading progress, mobile only. A long policy on a phone gives no sense of
 *     how much is left; the hairline under the sticky bar does.
 *   • Its own measured height, published as --pag-legal-bar, so a chapter
 *     opened from the sticky bar lands under it rather than behind it.
 *   • Smooth, focus-moving jumps to a chapter, so a keyboard user who picks a
 *     clause is reading it rather than still tabbing through the contents.
 *
 * The spy's "which chapter is current" rule and the scroller handling are the
 * same as assets/pag-pdp-subnav.js — see that file for why the observer has to
 * be rebuilt rather than reconfigured at the 990px boundary. Where the two part
 * company is the reading line: that one measures its bar, this one reads the
 * scroll-margin the chapters already carry. See readingLine below.
 */

import {
  getIntersectionRoot,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const SECTION_SELECTOR = '.pag-legal';
const LINK_SELECTOR = '.pag-legal__toc-link[href^="#"]';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Scroll behaviour for anything this file initiates. */
const behavior = () => (reducedMotion.matches ? 'auto' : 'smooth');

const clamp = (value) => Math.min(1, Math.max(0, value));

/**
 * The line a chapter has to reach to count as the one being read, in viewport
 * coordinates.
 *
 * Read off a chapter's own scroll-margin-top rather than rebuilt from the
 * header and the bar. That margin is by definition where a chapter comes to
 * rest when it is opened from the contents, so taking it as the line makes the
 * two agree exactly instead of approximately — a clicked chapter lands on the
 * line and lights up its own entry, at both breakpoints, with no arithmetic
 * here to keep in step with the stylesheet.
 *
 * The root offset is what converts it: scroll-margin is measured from the top
 * of the scrollport, and at 990px and up the scrollport is `.page-wrapper`
 * rather than the viewport.
 */
function readingLine(chapter, root) {
  const margin = parseFloat(getComputedStyle(chapter).scrollMarginTop) || 0;
  return (root ? root.getBoundingClientRect().top : 0) + margin;
}

/**
 * Publishes the contents bar's height so a clicked chapter clears it.
 *
 * Zero from 990px up: there the rail sits beside the text rather than over it,
 * and adding its height to every chapter's scroll-margin would drop each one
 * most of a screen too low.
 */
function publishBarHeight(section, nav) {
  const set = () => {
    const height = scrollContainerMediaQuery.matches ? 0 : nav.offsetHeight;
    section.style.setProperty('--pag-legal-bar', `${height}px`);
  };

  set();
  scrollContainerMediaQuery.addEventListener('change', set);

  if ('ResizeObserver' in window) new ResizeObserver(set).observe(nav);
  else window.addEventListener('resize', set, { passive: true });
}

/**
 * Keeps the current entry visible inside the rail itself.
 *
 * Both orientations need it and for the same reason: the chip row is wider than
 * a phone, and the desktop rail scrolls internally once the viewport is short
 * enough that twelve entries do not fit under the header. Scrolling the rail is
 * done by hand rather than with scrollIntoView, which would also scroll the
 * page and fight whatever put the reader here.
 */
function revealInRail(list, link) {
  if (list.scrollWidth > list.clientWidth + 1) {
    list.scrollTo({
      left: link.offsetLeft - (list.clientWidth - link.offsetWidth) / 2,
      behavior: behavior(),
    });
  }

  const rail = list.parentElement;
  if (rail && rail.scrollHeight > rail.clientHeight + 1) {
    rail.scrollTo({
      top: link.offsetTop - (rail.clientHeight - link.offsetHeight) / 2,
      behavior: behavior(),
    });
  }
}

/**
 * Highlights the entry whose chapter currently occupies the reading position.
 *
 * @returns {(() => void) | undefined} A re-check, so the click handler can catch
 *   the highlight up once a jump has landed.
 */
function bindScrollSpy(nav, links, line) {
  const targets = new Map();
  links.forEach((link) => {
    const id = decodeURIComponent(link.getAttribute('href').slice(1));
    const chapter = id && document.getElementById(id);
    if (chapter) targets.set(chapter, link);
  });
  if (targets.size < 2) return undefined;

  const list = nav.querySelector('.pag-legal__toc-list');
  let current = null;

  const setCurrent = (link) => {
    if (link === current) return;
    current = link;

    links.forEach((other) => {
      const isCurrent = other === link;
      other.classList.toggle('is-current', isCurrent);
      if (isCurrent) other.setAttribute('aria-current', 'location');
      else other.removeAttribute('aria-current');
    });

    if (list) revealInRail(list, link);
  };

  let observer;
  const visible = new Set();

  // Current is the LAST chapter whose top has reached the line, not the
  // highest one on screen: adjacent chapters share an edge, so taking the
  // highest hands the highlight to the chapter you just left. 1px of slack
  // because a clicked anchor lands via scroll-margin and routinely resolves a
  // fraction of a pixel short.
  const update = () => {
    if (!visible.size) return;

    const reading = line();
    const ordered = [...visible].sort(
      (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
    );

    let landed = null;
    for (const chapter of ordered) {
      if (chapter.getBoundingClientRect().top - reading <= 1) landed = chapter;
      else break;
    }

    // Nothing has reached the line yet — still in the masthead, so the topmost
    // visible chapter is the best answer.
    setCurrent(targets.get(landed || ordered[0]));
  };

  const build = () => {
    observer?.disconnect();
    visible.clear();

    // Only has to be roughly right: it sizes the band that decides which
    // chapters make it into `visible`, not the line they are judged against.
    const root = getIntersectionRoot();
    const offset = Math.round(line() - (root ? root.getBoundingClientRect().top : 0));

    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        }
        update();
      },
      { root, rootMargin: `-${Math.max(offset, 0)}px 0px -70% 0px` }
    );

    targets.forEach((_, chapter) => observer.observe(chapter));
  };

  // The masthead fills the observation band on arrival, so nothing has entered
  // it yet and `update` has nothing to go on. Starting on the first chapter
  // means the rail is never drawn blank, and the observer takes it from there
  // on the first scroll.
  setCurrent(links[0]);

  build();
  // An observer's root is fixed at construction, and the root itself changes at
  // 990px, so it has to be rebuilt there.
  scrollContainerMediaQuery.addEventListener('change', build);

  return update;
}

/**
 * Drives the progress hairline under the mobile contents bar.
 *
 * Measured across the chapters rather than the whole section: the masthead is
 * not something you read your way through, and counting it would have the bar
 * already part filled before the first word.
 */
function bindProgress(nav, meter, line) {
  const body = nav.parentElement?.querySelector('.pag-legal__body');
  if (!body) return undefined;

  return () => {
    const rect = body.getBoundingClientRect();
    const reading = line();
    const travel = rect.height - (window.innerHeight - reading);
    const done = travel > 0 ? (reading - rect.top) / travel : 1;
    meter.style.transform = `scaleX(${clamp(done)})`;
  };
}

/**
 * Jumps to a chapter, moving focus with the view.
 *
 * The default here is a plain fragment navigation, which already lands
 * correctly via scroll-margin. What it does not do is take focus with it: a
 * keyboard user would be looking at "Cookies" while still tabbing through the
 * contents list. The chapter carries tabindex="-1" for that, and preventScroll
 * keeps focus() from fighting the scroll already in flight.
 */
function bindJumps(links, settle) {
  links.forEach((link) => {
    link.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;

      const id = decodeURIComponent(link.getAttribute('href').slice(1));
      const chapter = id && document.getElementById(id);
      if (!chapter) return;

      event.preventDefault();
      chapter.scrollIntoView({ block: 'start', behavior: behavior() });
      chapter.focus({ preventScroll: true });

      // Keeps the clause shareable without stacking a history entry per jump.
      history.replaceState(null, '', `#${id}`);

      // The observer only reports crossings, and a smooth scroll is still in
      // flight here, so the highlight is caught up once it has settled.
      window.setTimeout(() => settle?.(), 400);
    });
  });
}

function setup(section) {
  const nav = section.querySelector('.pag-legal__toc');
  const links = [...section.querySelectorAll(LINK_SELECTOR)];
  const chapter = section.querySelector('.pag-legal__chapter');
  if (!nav || !chapter || links.length < 2) return;

  publishBarHeight(section, nav);

  // Every chapter carries the same scroll-margin, so any one of them answers
  // for all of them. Recomputed per call rather than cached: the margin changes
  // with the breakpoint, and the root's own offset changes as the page scrolls.
  const line = () => readingLine(chapter, getIntersectionRoot());

  const update = bindScrollSpy(nav, links, line);

  const meter = section.querySelector('.pag-legal__progress-meter');
  const progress = meter ? bindProgress(nav, meter, line) : undefined;

  const settle = () => {
    update?.();
    progress?.();
  };

  bindJumps(links, settle);
  settle();

  // The observer above reports crossings, not frames, and the progress meter
  // has no observer at all. One rAF-throttled listener drives both.
  let queued = false;
  getScrollEventTarget().addEventListener(
    'scroll',
    () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        settle();
      });
    },
    { passive: true }
  );

  window.addEventListener('resize', settle, { passive: true });
}

const init = (scope = document) => {
  scope.querySelectorAll?.(SECTION_SELECTOR).forEach((section) => {
    if (section.dataset.legalBound === 'true') return;
    section.dataset.legalBound = 'true';
    setup(section);
  });
};

init();

// The theme editor re-renders sections in place.
document.addEventListener('shopify:section:load', (event) => init(event.target));
